import { addChoice, rankChoices } from "@loot-os/api-contract/shopifyRuleBoxForm";
import { Badge } from "@loot-os/ui/components/badge";
import { Button } from "@loot-os/ui/components/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@loot-os/ui/components/command";
import { Label } from "@loot-os/ui/components/label";
import { Popover, PopoverContent, PopoverTrigger } from "@loot-os/ui/components/popover";
import { Plus, X } from "lucide-react";
import { useState } from "react";

/**
 * A multi-select over the linked shop's own values (tags, vendors, product types), the
 * dashboard's twin of the embedded admin's ChoicePicker: the chosen ones as removable chips and
 * a searchable list of the rest. With `freeText`, a value the shop does not use yet can be added
 * too (a tag the merchant is about to give products); without it, only one of `options`.
 */
export function RuleChoiceField({
  id,
  label,
  description,
  noun,
  options,
  chosen,
  freeText,
  disabled,
  onChange,
}: {
  id: string;
  label: string;
  description: string;
  /** Plural, for the button and the empty list: "tags", "vendors". */
  noun: string;
  options: string[];
  chosen: string[];
  freeText: boolean;
  disabled?: boolean;
  onChange: (chosen: string[]) => void;
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const matches = rankChoices(options, chosen, query);
  const typed = query.trim();
  const canAddTyped =
    freeText &&
    typed !== "" &&
    !chosen.some((v) => v.toLowerCase() === typed.toLowerCase()) &&
    !matches.some((v) => v.toLowerCase() === typed.toLowerCase());
  const pick = (value: string) => {
    onChange(addChoice(chosen, value));
    setQuery("");
  };

  return (
    <div className="flex flex-col gap-2">
      <Label htmlFor={id}>{label}</Label>
      <div className="flex flex-wrap items-center gap-1.5">
        {chosen.map((value) => (
          <Badge key={value} variant="secondary" className="gap-1 pr-1">
            {value}
            {!disabled && (
              <button
                type="button"
                aria-label={`Remove ${value}`}
                className="rounded-sm opacity-60 hover:opacity-100"
                onClick={() => onChange(chosen.filter((v) => v !== value))}
              >
                <X className="size-3" />
              </button>
            )}
          </Badge>
        ))}
        <Popover
          open={open}
          onOpenChange={(next) => {
            setOpen(next);
            if (!next) setQuery("");
          }}
        >
          <PopoverTrigger
            render={
              <Button
                id={id}
                type="button"
                variant="outline"
                size="xs"
                disabled={disabled}
                className="font-normal"
              />
            }
          >
            <Plus />
            {chosen.length ? "Add" : `Choose ${noun}`}
          </PopoverTrigger>
          <PopoverContent className="w-72 p-0" align="start">
            {/* cmdk's own matching is off: rankChoices already filtered and ordered the rows. */}
            <Command shouldFilter={false}>
              <CommandInput
                placeholder={freeText ? `Search or type new ${noun}…` : `Search ${noun}…`}
                value={query}
                onValueChange={setQuery}
              />
              <CommandList>
                <CommandEmpty>
                  {options.length ? `No matching ${noun}.` : `The shop has no ${noun} yet.`}
                </CommandEmpty>
                <CommandGroup>
                  {canAddTyped && (
                    <CommandItem value={`add:${typed}`} onSelect={() => pick(typed)}>
                      <Plus />
                      Add "{typed}"
                    </CommandItem>
                  )}
                  {matches.map((value) => (
                    <CommandItem key={value} value={value} onSelect={() => pick(value)}>
                      {value}
                    </CommandItem>
                  ))}
                </CommandGroup>
              </CommandList>
            </Command>
          </PopoverContent>
        </Popover>
      </div>
      <p className="text-sm text-muted-foreground">{description}</p>
    </div>
  );
}
